import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Link } from '@tanstack/react-router'
import { CheckCircle, AlertTriangle, User, FileText, MessageSquare, CalendarDays, XCircle } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useAlertDetail } from '../hooks/use-insights'
import { RELATIONSHIP_LABELS } from '@/features/shared/constants/relationships'
import type { RelationshipType } from '@/features/shared/types/patient.types'

interface AlertModalProps {
  insightId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreateEvolution?: (alert: any) => void
  onScheduleSession?: (alert: any) => void
  onDismiss?: (alert: any) => void
  isActing?: boolean
}

const severityColors: Record<string, string> = {
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

const severityLabels: Record<string, string> = {
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
}

const actionTypeLabels: Record<string, string> = {
  evolution_created: 'Evolução criada',
  session_scheduled: 'Sessão agendada',
  dismissed: 'Dispensado',
}

const dismissReasonLabels: Record<string, string> = {
  already_aware: 'Já estou ciente',
  part_of_condition: 'Faz parte do quadro',
  already_resolved: 'Já foi resolvido',
}

export function InsightModal({
  insightId,
  open,
  onOpenChange,
  onCreateEvolution,
  onScheduleSession,
  onDismiss,
  isActing,
}: AlertModalProps) {
  const { data: alert, isLoading } = useAlertDetail(insightId ?? '')

  if (!insightId) return null

  const severity = (alert?.severity as keyof typeof severityColors) ?? 'medium'
  const isActive = alert?.status === 'active'
  const sourceReport = alert?.source_report as any
  const reporter = sourceReport?.reporter as { relationship: string; profiles: { full_name: string } } | null
  const reporterLabel = reporter?.profiles?.full_name
    ? `${reporter.profiles.full_name} (${RELATIONSHIP_LABELS[reporter.relationship as RelationshipType] ?? reporter.relationship})`
    : null
  const actedByProfile = alert?.acted_by_profile as { full_name: string } | null
  const weeklySummary = alert?.weekly_summaries as { id: string; summary_text: string; week_start_date: string; week_end_date: string } | null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Detalhes do Alerta
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : alert ? (
          <div className="space-y-6">
            {/* Header info */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={severityColors[severity] ?? 'bg-gray-100 text-gray-800'}>
                {severityLabels[severity] ?? severity}
              </Badge>
              {alert.category && <Badge variant="outline">{alert.category}</Badge>}
              {!isActive && (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {actionTypeLabels[alert.action_type ?? ''] ?? 'Resolvido'}
                </Badge>
              )}
            </div>

            {/* Patient link */}
            {alert.patients && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <Link
                  to="/patients/$patientId"
                  params={{ patientId: (alert.patients as { id: string; full_name: string }).id }}
                  className="font-medium hover:underline"
                  onClick={() => onOpenChange(false)}
                >
                  {(alert.patients as { id: string; full_name: string }).full_name}
                </Link>
              </div>
            )}

            {/* Alert text */}
            <div>
              <h4 className="font-medium mb-2">Alerta</h4>
              <p className="text-muted-foreground">{alert.insight_text}</p>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Confiança:</span>{' '}
                <span className="font-medium">{Math.round((alert.ai_confidence ?? 0) * 100)}%</span>
              </div>
              <div>
                <span className="text-muted-foreground">Criado em:</span>{' '}
                <span className="font-medium">
                  {format(new Date(alert.created_at), "d 'de' MMMM, yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              </div>
            </div>

            {/* Source report */}
            {sourceReport && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Relato Original
                </h4>
                <div className="p-3 bg-muted rounded-lg">
                  {reporterLabel && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {reporterLabel}
                      {' — '}
                      {format(new Date(sourceReport.created_at), "d 'de' MMMM, yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{sourceReport.content}</p>
                </div>
              </div>
            )}

            {/* Related weekly summary (for legacy insights) */}
            {weeklySummary && !sourceReport && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Resumo Semanal Relacionado
                </h4>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">
                    {format(new Date(weeklySummary.week_start_date), "d 'de' MMMM", { locale: ptBR })}
                    {' - '}
                    {format(new Date(weeklySummary.week_end_date), "d 'de' MMMM, yyyy", { locale: ptBR })}
                  </p>
                  <p className="text-sm line-clamp-4">{weeklySummary.summary_text}</p>
                </div>
              </div>
            )}

            {/* Resolution info */}
            {!isActive && alert.acted_at && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Resolução
                </h4>
                <div className="p-3 bg-green-50 rounded-lg text-sm space-y-1">
                  <p>
                    <span className="font-medium">{actionTypeLabels[alert.action_type ?? ''] ?? 'Resolvido'}</span>
                    {actedByProfile && ` por ${actedByProfile.full_name}`}
                  </p>
                  <p className="text-muted-foreground">
                    {format(new Date(alert.acted_at), "d 'de' MMMM, yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                  {alert.dismiss_reason && (
                    <p className="text-muted-foreground">
                      Motivo: {dismissReasonLabels[alert.dismiss_reason] ?? alert.dismiss_reason}
                    </p>
                  )}
                  {alert.action_note && (
                    <p className="text-muted-foreground italic">"{alert.action_note}"</p>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {isActive && (
              <div className="flex justify-end gap-2 pt-4 border-t">
                {onDismiss && (
                  <Button
                    variant="outline"
                    onClick={() => { onDismiss(alert); onOpenChange(false) }}
                    disabled={isActing}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Dispensar
                  </Button>
                )}
                {onScheduleSession && (
                  <Button
                    variant="outline"
                    onClick={() => { onScheduleSession(alert); onOpenChange(false) }}
                    disabled={isActing}
                  >
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Agendar Sessão
                  </Button>
                )}
                {onCreateEvolution && (
                  <Button
                    onClick={() => { onCreateEvolution(alert); onOpenChange(false) }}
                    disabled={isActing}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Criar Evolução
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            Alerta não encontrado
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

// Legacy export
export { InsightModal as AlertModal }
